// A minimal, dependency-free .xlsx writer.
//
// Why hand-rolled rather than SheetJS/exceljs: the whole need is "a few sheets
// of text, numbers and percentages with bold headers and frozen panes". The
// smallest library that does that is two orders of magnitude larger than this
// file, and the admin bundle currently carries five runtime dependencies
// total. The format surface used here is deliberately tiny: inline strings (no
// sharedStrings part), a fixed style table, and STORED (uncompressed) zip
// entries.
//
// STORED, not DEFLATE, because compressing would mean shipping a deflate
// implementation -- the one part of the format that is genuinely hard. Excel,
// LibreOffice, Google Sheets and openpyxl all accept method-0 entries. The
// cost is file size. Large individual-response downloads use the chunked
// writer in xlsxStreaming.ts; this path builds small analytics reports.
//
// Two properties worth keeping if this is edited:
//   * The output is BYTE-DETERMINISTIC (fixed 1980 DOS timestamp), so tests
//     can assert on it and two exports of the same data compare equal.
//   * `buildWorkbookParts` is exported separately from `zipStore` so tests can
//     assert on readable XML instead of unzipping bytes.

/** Style slots in the fixed `cellXfs` table below. */
export type CellStyle =
  | "default"
  | "header"
  | "title"
  | "label"
  | "muted"
  | "wrap"
  | "mutedWrap"
  | "text"
  | "int"
  | "pct1"
  | "pct2"
  | "groupRow"
  | "bar";

const STYLE_INDEX: Record<CellStyle, number> = {
  default: 0,
  header: 1,
  title: 2,
  label: 3,
  muted: 4,
  wrap: 5,
  mutedWrap: 6,
  text: 7,
  int: 8,
  pct1: 9,
  pct2: 10,
  groupRow: 11,
  bar: 12,
};

export type Cell =
  | null
  | string
  | number
  | { v: string | number | null; s?: CellStyle };

export type Row = {
  cells: Cell[];
  /** Explicit row height in points. Needed for wrapped multi-line cells. */
  height?: number;
};

export type Sheet = {
  name: string;
  rows: Row[];
  /** Column widths in Excel character units, left to right. */
  columns?: number[];
  /** Cell above-and-left of which everything is frozen, e.g. "D2". */
  freeze?: string;
  /** Range for the filter dropdowns, e.g. "A1:I13". */
  autoFilter?: string;
  merges?: string[];
  showGridLines?: boolean;
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ---------------------------------------------------------------- XML helpers

// Tab, newline and carriage return are the only control characters XML 1.0
// allows. Anything below 0x20 that is not one of them makes Excel declare the
// whole file corrupt rather than mangling one cell, so it is dropped. Written
// as a codepoint scan and not a regex on purpose: the escape sequence for that
// character class is itself easy to corrupt in source, and a class that turns
// into its own negation silently strips every printable character instead.
function stripControls(value: string): string {
  let out = "";
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) continue;
    out += ch;
  }
  return out;
}

function esc(value: string): string {
  return stripControls(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\r/g, "&#13;");
}

/** 1 -> "A", 27 -> "AA". */
export function columnName(index: number): string {
  let n = index;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function normalize(cell: Cell): { v: string | number | null; s: CellStyle } {
  if (cell === null) return { v: null, s: "default" };
  if (typeof cell === "string" || typeof cell === "number") {
    return { v: cell, s: "default" };
  }
  return { v: cell.v ?? null, s: cell.s ?? "default" };
}

function cellXml(ref: string, cell: Cell): string {
  const { v, s } = normalize(cell);
  const style = STYLE_INDEX[s];
  const sAttr = style === 0 ? "" : ` s="${style}"`;
  if (v === null || v === "") return `<c r="${ref}"${sAttr}/>`;
  if (typeof v === "number") {
    // A non-finite number has no cell representation; write it as text rather
    // than emitting `<v>NaN</v>`, which makes Excel declare the file corrupt.
    if (!Number.isFinite(v)) {
      return `<c r="${ref}"${sAttr} t="inlineStr"><is><t>${esc(String(v))}</t></is></c>`;
    }
    return `<c r="${ref}"${sAttr}><v>${v}</v></c>`;
  }
  return (
    `<c r="${ref}"${sAttr} t="inlineStr">` +
    `<is><t xml:space="preserve">${esc(v)}</t></is></c>`
  );
}

function freezePane(ref: string): string {
  const m = /^([A-Z]+)(\d+)$/.exec(ref);
  const letters = m?.[1];
  const digits = m?.[2];
  if (!letters || !digits) return "";
  let col = 0;
  for (const ch of letters) col = col * 26 + (ch.charCodeAt(0) - 64);
  const xSplit = col - 1;
  const ySplit = Number(digits) - 1;
  const parts = [
    xSplit > 0 ? `xSplit="${xSplit}"` : "",
    ySplit > 0 ? `ySplit="${ySplit}"` : "",
    `topLeftCell="${ref}"`,
    `activePane="bottomRight"`,
    `state="frozen"`,
  ].filter(Boolean);
  return `<pane ${parts.join(" ")}/>`;
}

/** Shared serialization keeps chunked response exports identical to reports. */
export function xlsxRowXml(row: Row, rowNumber: number): string {
  const ht = row.height ? ` ht="${row.height}" customHeight="1"` : "";
  const cells = row.cells
    .map((cell, index) => cellXml(`${columnName(index + 1)}${rowNumber}`, cell))
    .join("");
  return `<row r="${rowNumber}"${ht}>${cells}</row>`;
}

/** Worksheet XML around sheetData, whose rows may already be stored in Blobs. */
export function worksheetFrame(
  sheet: Omit<Sheet, "rows">,
  rowCount: number,
  columnCount: number,
): { prefix: string; suffix: string } {
  const lastCol = columnName(Math.max(1, columnCount));
  const cols = sheet.columns?.length
    ? "<cols>" +
      sheet.columns
        .map(
          (w, i) =>
            `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`,
        )
        .join("") +
      "</cols>"
    : "";
  const gridAttr = sheet.showGridLines === false ? ' showGridLines="0"' : "";
  const view =
    `<sheetViews><sheetView${gridAttr} workbookViewId="0">` +
    (sheet.freeze ? freezePane(sheet.freeze) : "") +
    "</sheetView></sheetViews>";
  const merges = sheet.merges?.length
    ? `<mergeCells count="${sheet.merges.length}">` +
      sheet.merges.map((ref) => `<mergeCell ref="${ref}"/>`).join("") +
      "</mergeCells>"
    : "";
  // Element order below follows the CT_Worksheet sequence -- autoFilter before
  // mergeCells. Excel refuses to open the file if these are transposed.
  return {
    prefix:
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
      `<dimension ref="A1:${lastCol}${Math.max(1, rowCount)}"/>` +
      view +
      `<sheetFormatPr defaultRowHeight="15"/>` +
      cols +
      `<sheetData>`,
    suffix:
      `</sheetData>` +
      (sheet.autoFilter ? `<autoFilter ref="${sheet.autoFilter}"/>` : "") +
      merges +
      `</worksheet>`,
  };
}

function sheetXml(sheet: Sheet): string {
  const width = sheet.rows.reduce((m, r) => Math.max(m, r.cells.length), 0);
  const { prefix, suffix } = worksheetFrame(sheet, sheet.rows.length, width);
  return (
    prefix +
    sheet.rows.map((row, index) => xlsxRowXml(row, index + 1)).join("") +
    suffix
  );
}

const STYLES_XML =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  `<numFmts count="3">` +
  `<numFmt numFmtId="164" formatCode="#,##0"/>` +
  `<numFmt numFmtId="165" formatCode="0.0%"/>` +
  `<numFmt numFmtId="166" formatCode="0.00%"/>` +
  `</numFmts>` +
  `<fonts count="6">` +
  `<font><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="16"/><color rgb="FFFF5600"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="11"/><name val="Calibri"/></font>` +
  `<font><sz val="10"/><color rgb="FF666666"/><name val="Calibri"/></font>` +
  `<font><sz val="9"/><color rgb="FFFF5600"/><name val="Calibri"/></font>` +
  `</fonts>` +
  // Fill slots 0 (none) and 1 (gray125) are mandated by the format; custom
  // fills must start at index 2 or Excel renders the wrong colours.
  `<fills count="4">` +
  `<fill><patternFill patternType="none"/></fill>` +
  `<fill><patternFill patternType="gray125"/></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FF1A1A1A"/><bgColor indexed="64"/></patternFill></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FFF2F2F2"/><bgColor indexed="64"/></patternFill></fill>` +
  `</fills>` +
  `<borders count="2">` +
  `<border><left/><right/><top/><bottom/><diagonal/></border>` +
  `<border><left/><right/><top/><bottom style="thin"><color rgb="FFD9D9D9"/></bottom><diagonal/></border>` +
  `</borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="13">` +
  /* 0 default   */ `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
  /* 1 header    */ `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>` +
  /* 2 title     */ `<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  /* 3 label     */ `<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  /* 4 muted     */ `<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  /* 5 wrap      */ `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>` +
  /* 6 mutedWrap */ `<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>` +
  /* 7 text      */ `<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>` +
  /* 8 int       */ `<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>` +
  /* 9 pct1      */ `<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>` +
  /* 10 pct2     */ `<xf numFmtId="166" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>` +
  /* 11 groupRow */ `<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>` +
  /* 12 bar      */ `<xf numFmtId="0" fontId="5" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>` +
  `</cellXfs>` +
  // The "Normal" named style. Optional in the schema and easy to leave out,
  // but a stylesheet without it makes readers substitute their own default --
  // openpyxl warns, and Excel is entitled to reformat the whole sheet.
  `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
  `</styleSheet>`;

export type WorkbookPart = { name: string; xml: string };

/** The OOXML parts of the workbook, unzipped. Exported for tests. */
export function buildWorkbookParts(sheets: Sheet[]): WorkbookPart[] {
  if (sheets.length === 0) throw new Error("a workbook needs at least one sheet");
  const sheetPaths = sheets.map((_, i) => `xl/worksheets/sheet${i + 1}.xml`);
  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    sheetPaths
      .map(
        (p) =>
          `<Override PartName="/${p}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
      )
      .join("") +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `</Types>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>` +
    sheets
      .map(
        (s, i) =>
          `<sheet name="${esc(sheetName(s.name))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
      )
      .join("") +
    `</sheets></workbook>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    sheets
      .map(
        (_, i) =>
          `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
      )
      .join("") +
    `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

  return [
    { name: "[Content_Types].xml", xml: contentTypes },
    { name: "_rels/.rels", xml: rootRels },
    { name: "xl/workbook.xml", xml: workbook },
    { name: "xl/_rels/workbook.xml.rels", xml: workbookRels },
    { name: "xl/styles.xml", xml: STYLES_XML },
    ...sheets.map((s, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      xml: sheetXml(s),
    })),
  ];
}

/** Excel caps sheet names at 31 chars and forbids `: \ / ? * [ ]`. */
function sheetName(name: string): string {
  const cleaned = name.replace(/[:\\/?*[\]]/g, " ").trim();
  return (cleaned || "Sheet").slice(0, 31);
}

// -------------------------------------------------------------------- the zip

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = (CRC_TABLE[(c ^ (bytes[i] ?? 0)) & 0xff] ?? 0) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// A fixed 1980-01-01 00:00 DOS timestamp keeps the output byte-deterministic:
// two exports of the same report are identical files, and tests can assert on
// bytes. A real mtime would buy nothing -- the date range is in the report.
const DOS_TIME = 0;
const DOS_DATE = 0x0021;

/**
 * A ZIP archive with every entry STORED (no compression).
 *
 * Returns bytes rather than a Blob so it stays testable: jsdom's Blob has no
 * `arrayBuffer()`, so a test that had to unwrap one could not read what this
 * wrote at all.
 */
export function zipStoreBytes(files: { name: string; data: Uint8Array }[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = new TextEncoder().encode(file.name);
    const crc = crc32(file.data);
    const size = file.data.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method 0 = stored
    lv.setUint16(10, DOS_TIME, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size
    lv.setUint32(22, size, true); // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true); // extra length
    local.set(nameBytes, 30);
    chunks.push(local, file.data);

    const cd = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true); // flags
    cv.setUint16(10, 0, true); // method
    cv.setUint16(12, DOS_TIME, true);
    cv.setUint16(14, DOS_DATE, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk number start
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true);
    cd.set(nameBytes, 46);
    central.push(cd);

    offset += local.length + size;
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);

  const all = [...chunks, ...central, end];
  const total = all.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of all) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

export function zipStore(files: { name: string; data: Uint8Array }[]): Blob {
  return toBlob(zipStoreBytes(files));
}

// `zipStoreBytes` allocates its own exactly-sized buffer, so handing the
// buffer to Blob is safe. The cast is only to satisfy the DOM lib, which
// types `Uint8Array` as backed by `ArrayBufferLike` (it could be a
// SharedArrayBuffer) while `BlobPart` requires a plain `ArrayBuffer`.
function toBlob(bytes: Uint8Array): Blob {
  return new Blob([bytes.buffer as ArrayBuffer], { type: XLSX_MIME });
}

/** Render sheets to the bytes of an .xlsx file. */
export function buildXlsxBytes(sheets: Sheet[]): Uint8Array {
  const encoder = new TextEncoder();
  return zipStoreBytes(
    buildWorkbookParts(sheets).map((p) => ({
      name: p.name,
      data: encoder.encode(p.xml),
    })),
  );
}

/** Render sheets to a downloadable .xlsx Blob. */
export function buildXlsx(sheets: Sheet[]): Blob {
  return toBlob(buildXlsxBytes(sheets));
}
